'use client';

import { motion } from 'motion/react';
import { SectionLayout } from '@/components/shared/section-layout';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import { SanityInlineText } from '@/components/shared/sanity-text';
import ApproachCard from '@/components/sections/approach/card';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import type { ApproachSectionContent } from '@/sanity/lib/sections';

export default function OurApproach({ content }: { content?: ApproachSectionContent | null }) {
  const {
    sectionGridRevealVariants,
    sectionHeaderItemVariants,
    sectionHeaderRevealVariants,
    sectionSequenceRevealVariants,
  } = useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();
  const { header, ctaButtons } = content ?? {};
  const cards = content?.showCards === false ? [] : (content?.cards ?? []);
  const { primary, secondary } = sectionCtaPairFromContent(content?.showCTA, ctaButtons);
  const showHeader = content?.showHeader !== false;

  if (!content || content.show === false) return null;

  return (
    <SectionLayout
      id="our-approach"
      className="relative scroll-mt-14 sm:scroll-mt-0 bg-neutral-50 dark:bg-neutral-950 py-6 sm:py-12 xl:py-16 2xl:py-24"
      containerClassName="mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0"
    >
      <motion.div
        className="grid grid-cols-1 gap-6 lg:grid-cols-2 sm:gap-12 xl:gap-16 2xl:gap-24"
        {...scrollReveal}
        variants={sectionSequenceRevealVariants}
      >
        <motion.div
          variants={sectionHeaderRevealVariants}
          className="lg:sticky lg:top-32 lg:self-start space-y-3"
        >
          {showHeader ? (
            <>
              {header?.tagline ? (
                <motion.div
                  variants={sectionHeaderItemVariants}
                  className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-[#948B7F] dark:text-[#FCFAD7]"
                >
                  {header.tagline}
                </motion.div>
              ) : null}
              <motion.h2
                variants={sectionHeaderItemVariants}
                className="text-3xl sm:text-5xl leading-snug text-foreground"
              >
                <SanityInlineText
                  blocks={header?.heading}
                  emphasisClassName="italic font-normal text-[#948B7F] dark:text-[#FCFAD7]"
                />
              </motion.h2>
              {header?.subheading ? (
                <motion.p
                  variants={sectionHeaderItemVariants}
                  className="max-w-3xl text-sm sm:text-base leading-relaxed font-light text-muted-foreground"
                >
                  {header.subheading}
                </motion.p>
              ) : null}
            </>
          ) : null}
          {primary ? (
            <motion.div variants={sectionHeaderItemVariants}>
              <SectionCtaPair primary={primary} secondary={secondary} trackingLocation="approach" />
            </motion.div>
          ) : null}
        </motion.div>
        {cards.length ? (
          <motion.div variants={sectionGridRevealVariants} className="space-y-3">
            {cards.map(({ iconKey, title, description }) => {
              if (!title || !description || !iconKey) return null;

              return (
                <ApproachCard
                  key={title}
                  iconName={iconKey}
                  title={title}
                  description={description}
                />
              );
            })}
          </motion.div>
        ) : null}
      </motion.div>
    </SectionLayout>
  );
}
